// src/components/EligibilityFilter10.js
import React, { useState } from "react";
import {
  CREDIT_TYPES,
  evaluateEligibility,
} from "../config/eligibilityConfig";
import Header from "../Header/Header";
import "./EligibilityFilter.css";

const STATUS_EMOJI = {
  yes: "✅",
  no: "❌",
  maybe: "⚠️",
};

// Generic fallback labels
const STATUS_LABEL = {
  yes: "Likely eligible based on these 10 questions.",
  no: "Likely NOT eligible based on these questions.",
  maybe: "Needs more detail beyond this quick filter.",
};

// Credit-specific explanations when we land on MAYBE
// (ids must match your CREDIT_TYPES config)
const CREDIT_MAYBE_NOTES = {
  savers:
    "You passed the basic gates this tool can check (SSN, earned income, retirement contribution, student rules). Saver's Credit now depends on your exact income numbers and filing status. Use the Saver's Credit module along with IRS publications for a clearer answer.",
  eitc:
    "Basic gates look okay for EITC, but the exact result depends on filing status, precise earned income, and the number of qualifying children. Use the EITC module along with IRS publications for a clearer answer.",
  aotc:
    "You may meet the basic conditions for the American Opportunity Credit, but the final answer depends on your modified AGI, filing status, and how scholarships are applied. Use the Education module along with IRS publications for a clearer answer.",
  llc:
    "You may meet the basic conditions for the Lifetime Learning Credit, but eligibility still depends on your income limits and filing status. Use the Education module along with IRS publications for a clearer answer.",
  ctc:
    "You may meet basic rules for the Child Tax Credit, but the exact amount and eligibility depend on your income, filing status, and the number of qualifying children. Use the Child Tax module along with IRS publications for a clearer answer.",
  actc:
    "You might be in the range for the Additional Child Tax Credit, but it depends on your earned income, withholding, and how much of the regular CTC you can actually use. Use the Child Tax module along with IRS publications for a clearer answer.",
  odc:
    "You may qualify based on having other dependents, but the final outcome depends on income thresholds and how other credits and dependents interact on your return. Use the Other Dependent module along with IRS publications for a clearer answer.",
  cdc:
    "You may meet the high-level rules for the Child & Dependent Care Credit, but the final eligibility depends on work-related care expenses and detailed dollar amounts. Use the Child Tax module along with IRS publications for a clearer answer.",
  education:
    "You may qualify for an education credit based on tuition and enrollment, but the final answer depends on your AGI, filing status, and scholarship treatment. Use the Education module along with IRS publications for a clearer answer.",
};

/**
 * QUESTION CONFIGURATION
 * NOTE: labels here are WITHOUT “Q1.” etc.
 * We add Q-numbers dynamically when rendering.
 */
const QUESTION_CONFIG = [
  {
    id: "ssn",
    appliesTo: "all",
    label: "Do you (and spouse/child if relevant) have work-authorized SSNs?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "earnedIncome",
    appliesTo: "all",
    label: "Do you have earned income (wages, self-employment, etc.)?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "canBeClaimed",
    appliesTo: "all",
    label: "Can someone else claim you as a dependent on their return?",
    options: [
      { value: "yes", label: "Yes, someone can claim me" },
      { value: "no", label: "No, I cannot be claimed" },
    ],
  },
  {
    id: "ageBand",
    appliesTo: "all",
    label: "Your age band for this year?",
    options: [
      { value: "under18", label: "Under 18" },
      { value: "18to24", label: "18–24" },
      { value: "25to64", label: "25–64" },
      { value: "65plus", label: "65 or older" },
    ],
  },
  {
    id: "hasQualifyingChild",
    appliesTo: ["eitc", "ctc", "actc", "odc"],
    label:
      "Do you have at least one child who might be a qualifying child for credits?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "fullTimeStudent",
    appliesTo: ["eitc", "savers"],
    label: "Are you a full-time student this year?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "paidTuition",
    appliesTo: ["aotc", "llc", "education"],
    label:
      "Did you (or someone you claim) pay qualifying tuition/education expenses this year?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "retirementContribution",
    appliesTo: ["savers"],
    label:
      "Did you make contributions to a retirement account that might count for Saver's Credit (IRA, 401(k), etc.)?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "investmentIncomeHigh",
    appliesTo: ["eitc"],
    label:
      "Is your investment income above the EITC limit range (where EITC is cut off)?",
    options: [
      { value: "no", label: "No, it is below the EITC limit" },
      { value: "yes", label: "Yes, it is above the EITC limit" },
    ],
  },
  {
    id: "incomeLevel",
    appliesTo: "all",
    label: "Roughly where does your earned income sit for this year?",
    helper:
      "Very rough teaching bands only. Exact IRS thresholds depend on filing status and year.",
    options: [
      { value: "low", label: "Low" },
      { value: "medium", label: "Medium" },
      { value: "high", label: "High" },
    ],
  },
];

function questionIsRelevant(question, selectedCredits) {
  if (selectedCredits.length === 0) return false;
  if (question.appliesTo === "all") return true;
  return selectedCredits.some((id) => question.appliesTo.includes(id));
}

const EligibilityFilter = () => {
  // Start with NO credits selected so nothing fires until the user chooses
  const [selectedCredits, setSelectedCredits] = useState([]);

  const [answers, setAnswers] = useState({
    ssn: null,
    earnedIncome: null,
    canBeClaimed: null,
    ageBand: null,
    hasQualifyingChild: null,
    fullTimeStudent: null,
    paidTuition: null,
    retirementContribution: null,
    investmentIncomeHigh: null,
    incomeLevel: null,
  });

  const toggleCredit = (id) => {
    setSelectedCredits((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setAnswer = (field, value) =>
    setAnswers((prev) => ({ ...prev, [field]: value }));

  const visibleQuestions = QUESTION_CONFIG.filter((q) =>
    questionIsRelevant(q, selectedCredits)
  );
  const hasQuestions = visibleQuestions.length > 0;
  const hasAnyAnswer =
    hasQuestions && visibleQuestions.some((q) => answers[q.id] !== null);
  const allVisibleAnswered =
    hasQuestions && visibleQuestions.every((q) => answers[q.id] !== null);

  // Compute raw results once there's at least one answer
  const rawResults =
    selectedCredits.length > 0 && hasAnyAnswer
      ? evaluateEligibility(answers, selectedCredits)
      : {};

  // Build rows with status + note
  const selectedCreditList = CREDIT_TYPES.filter((c) =>
    selectedCredits.includes(c.id)
  );

  const allRows = selectedCreditList.map((credit) => {
    const info = rawResults[credit.id];
    const status = info?.status || "maybe";
    const maybeNote = status === "maybe" ? CREDIT_MAYBE_NOTES[credit.id] : null;
    const note =
      maybeNote !== null
        ? maybeNote
        : STATUS_LABEL[status] ?? STATUS_LABEL.maybe;

    return { credit, status, note };
  });

  // Before all questions answered:
  //  - if some credits are clearly NO, show only those rows
  //  - hide YES/MAYBE until income (and others) are answered
  const noRows = allRows.filter((r) => r.status === "no");

  const showOnlyNoRows = hasAnyAnswer && !allVisibleAnswered && noRows.length > 0;
  const showFullTable = hasAnyAnswer && allVisibleAnswered;

  return (
    <div className="elig-screen-bg">
      <Header />
      <div className="elig-container">
        {/* CREDIT PICKER */}
        <section className="elig-card">
          <h2 className="elig-card-title">
            Which credits do you want in this analysis?
          </h2>
          <p className="elig-card-subtitle">
            Turn credits on or off. This tool is for learning only, not tax
            advice.
          </p>

          <div className="elig-credit-grid">
            {CREDIT_TYPES.map((credit) => {
              const checked = selectedCredits.includes(credit.id);
              return (
                <label
                  key={credit.id}
                  className={`elig-credit-pill ${
                    checked ? "elig-credit-pill-on" : "elig-credit-pill-off"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCredit(credit.id)}
                    className="elig-credit-checkbox"
                  />
                  <span>
                    <strong>{credit.label}</strong>
                    <br />
                    <span className="elig-credit-long">{credit.long}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </section>

        {/* QUESTIONS */}
        <section className="elig-card">
          <h2 className="elig-card-title">10-question filter (eligibility gate)</h2>

          {selectedCredits.length === 0 ? (
            <p className="elig-no-credits">
              Turn on at least one credit above to see relevant questions here.
            </p>
          ) : !hasQuestions ? (
            <p className="elig-no-credits">
              The credits you selected don&apos;t use this quick filter yet.
            </p>
          ) : (
            visibleQuestions.map((q, index) => (
              <QuestionBlock
                key={q.id}
                label={`Q${index + 1}. ${q.label}`}
                helper={q.helper}
                field={q.id}
                answers={answers}
                setAnswer={setAnswer}
                options={q.options}
              />
            ))
          )}
        </section>

        {/* RESULTS */}
        <section className="elig-card">
          <h2 className="elig-card-title">High-level eligibility picture</h2>

          {selectedCredits.length === 0 ? (
            <p className="elig-no-credits">
              Turn on at least one credit above to see a summary here.
            </p>
          ) : !hasQuestions ? (
            <p className="elig-no-credits">
              The credits you selected don&apos;t use this 10-question filter.
            </p>
          ) : !hasAnyAnswer ? (
            <p className="elig-no-credits">
              Answer the questions above to see which credits are clearly ruled
              out and which might still be possible.
            </p>
          ) : showOnlyNoRows ? (
            <>
              <table className="elig-results-table">
                <thead>
                  <tr>
                    <th>Credit</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {noRows.map(({ credit, status, note }) => (
                    <tr key={credit.id}>
                      <td>
                        <strong>{credit.label}</strong>{" "}
                        <span className="elig-credit-long-inline">
                          ({credit.long})
                        </span>
                      </td>
                      <td className="elig-status-cell">
                        {STATUS_EMOJI[status]} {status.toUpperCase()}
                      </td>
                      <td>{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="elig-disclaimer">
                Based on your answers so far, these credits look unlikely. Keep
                answering the remaining questions (especially income) to see if
                any other credits might be a fit.
              </p>
            </>
          ) : showFullTable ? (
            <>
              <table className="elig-results-table">
                <thead>
                  <tr>
                    <th>Credit</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map(({ credit, status, note }) => (
                    <tr key={credit.id}>
                      <td>
                        <strong>{credit.label}</strong>{" "}
                        <span className="elig-credit-long-inline">
                          ({credit.long})
                        </span>
                      </td>
                      <td className="elig-status-cell">
                        {STATUS_EMOJI[status]} {status.toUpperCase()}
                      </td>
                      <td>{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="elig-disclaimer">
                This is a simplified, education-only filter. Real eligibility
                depends on exact income, filing status, residency, ages,
                support tests, and IRS rules for the specific year.
              </p>
            </>
          ) : (
            // hasAnyAnswer but noRows.length === 0 and not all answered:
            // i.e., nothing is ruled out yet; income & other missing details matter
            <p className="elig-no-credits">
              Nothing is ruled out yet. Your answers so far pass the basic
              gates. Finish the remaining questions—especially the income
              question—to see whether any credits look likely or unlikely.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

const QuestionBlock = ({
  label,
  field,
  answers,
  setAnswer,
  options,
  helper,
}) => (
  <div className="elig-question-block">
    <p className="elig-question-label">
      <strong>{label}</strong>
    </p>
    {helper && <p className="elig-question-helper">{helper}</p>}
    <div className="elig-radio-group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`elig-radio-btn ${
            answers[field] === opt.value ? "elig-radio-btn-active" : ""
          }`}
          onClick={() => setAnswer(field, opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

export default EligibilityFilter;
